// Prototype-pollution hardening for the app entry point.
//
// Defence-in-depth against prototype-pollution payloads landing through any
// user-supplied data we parse client-side (xlsx workbooks, JSON, etc.). The
// threat is a malicious `__proto__` / `constructor.prototype` write that ADDS
// an inherited key, silently poisoning every subsequent object lookup.
// `preventExtensions` makes adding any own property to these prototypes fail —
// a TypeError in strict callers, a safe no-op in sloppy ones — so no injected
// key can ever land.
//
// ⚠️ preventExtensions, NOT Object.freeze — the difference is load-bearing, and
// the unit test locks it. Object.freeze ALSO makes every existing prototype
// property non-writable, and in strict mode that turns any library's attempt to
// shadow an inherited method on its own prototype into a TypeError. pdf-lib does
// exactly that at module-init:
//     PDFHeader.prototype.toString = function () { … }
// which threw "Cannot assign to read only property 'toString'" once
// Object.prototype was frozen. Because pdf-lib is lazily imported (a code-split
// chunk), it loads AFTER this hardening runs — so freezing broke every pdf-lib
// path in production (badge sheets AND compliance-PDF downscale; Sentry
// f2492e6e, 2026-07-21). preventExtensions keeps the anti-pollution guarantee
// the audit wanted without freezing the method slots libraries legitimately
// shadow.
//
// RESIDUAL vs freeze: an attacker who can assign to an ALREADY-EXISTING
// Object.prototype method (e.g. `Object.prototype.toString = evil`) is not
// blocked, because existing properties stay writable. That is a narrower,
// separate vector from prototype pollution (which injects NEW keys — still
// blocked). Flagged for security review; see quality-playbook.
//
// Call ONCE, from the entry point, after static module init so statically-
// imported libraries that set prototype methods at import time have already
// run. Added 2026-05-16 (sec audit medium #2); freeze → preventExtensions
// 2026-07-21.

/** Harden Object.prototype + Array.prototype against new-key pollution. */
export function hardenPrototypes(): void {
  Object.preventExtensions(Object.prototype)
  Object.preventExtensions(Array.prototype)
}
